import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api, ApiError, type AdminSession } from "../lib/api";

// Admin landing page — lists the org's sessions and hosts the new-session
// form. Active (setup/open/paused) and past (closed) split into two
// sections; within each, backend orders by created_at DESC.

const STATE_META: Record<string, { color: string; label: string }> = {
  setup: { color: "#6b7280", label: "Not started" },
  open: { color: "#16a34a", label: "Open" },
  paused: { color: "#d97706", label: "Paused" },
  closed: { color: "#dc2626", label: "Closed" },
};

const ACTIVE_STATES = new Set(["setup", "open", "paused"]);

function StatePill({ state }: { state: string }) {
  const meta = STATE_META[state] ?? { color: "#6b7280", label: state };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0.1rem 0.55rem",
        borderRadius: "999px",
        background: meta.color,
        color: "white",
        fontWeight: 600,
        fontSize: "0.75rem",
        whiteSpace: "nowrap",
      }}
      aria-label={`State: ${meta.label}`}
    >
      ● {meta.label}
    </span>
  );
}

function SessionCard({ s }: { s: AdminSession }) {
  return (
    <li className="card" style={{ marginBottom: "0.5rem" }}>
      <div
        className="row"
        style={{ alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}
      >
        <Link
          to={`/admin/sessions/${s.id}`}
          style={{ fontWeight: 600, fontSize: "1.05rem" }}
        >
          {s.name}
        </Link>
        <StatePill state={s.state} />
        <span className="meta" style={{ fontSize: "0.85rem" }}>
          Code <code>{s.join_code}</code>
        </span>
      </div>
      <div
        className="meta"
        style={{ fontSize: "0.8rem", marginTop: "0.25rem" }}
      >
        Created {new Date(s.created_at).toLocaleString()}
        {s.starts_at && (
          <>
            {" · "}Scheduled {new Date(s.starts_at).toLocaleString()}
          </>
        )}
      </div>
    </li>
  );
}

export default function OrgSessionList() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState<AdminSession[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listSessions();
        if (!cancelled) setSessions(list);
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof ApiError ? e.message : "could not load sessions");
          setSessions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function create() {
    setErr(null);
    setBusy(true);
    try {
      const r = await api.createSession({ name: name.trim() });
      nav(`/admin/sessions/${r.id}`);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not create session");
    } finally {
      setBusy(false);
    }
  }

  const active = sessions?.filter((s) => ACTIVE_STATES.has(s.state)) ?? [];
  const past = sessions?.filter((s) => !ACTIVE_STATES.has(s.state)) ?? [];

  return (
    <main>
      <h1>Sessions</h1>

      <section style={{ marginBottom: "1.5rem" }}>
        <h2>New session</h2>
        <label htmlFor="sname">Session name</label>
        <input
          id="sname"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={256}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim() && !busy) create();
          }}
        />
        <p
          style={{
            color: "var(--fg-muted)",
            fontSize: "0.85rem",
            marginTop: "0.25rem",
          }}
        >
          You can set a scheduled start time on the next page.
        </p>
        {err && (
          <p className="error" role="alert">
            {err}
          </p>
        )}
        <button
          onClick={create}
          disabled={busy || !name.trim()}
          style={{ marginTop: "0.5rem" }}
        >
          {busy ? "Creating…" : "Create"}
        </button>
      </section>

      {sessions === null ? (
        <p className="meta">Loading…</p>
      ) : sessions.length === 0 ? (
        <p className="meta">No sessions yet — create your first above.</p>
      ) : (
        <>
          <section>
            <h2>Active ({active.length})</h2>
            {active.length === 0 ? (
              <p className="meta">No active sessions.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0 }}>
                {active.map((s) => (
                  <SessionCard key={s.id} s={s} />
                ))}
              </ul>
            )}
          </section>

          <section style={{ marginTop: "1.5rem" }}>
            <h2>Past ({past.length})</h2>
            {past.length === 0 ? (
              <p className="meta">No closed sessions.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0 }}>
                {past.map((s) => (
                  <SessionCard key={s.id} s={s} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
